<template>
  <div class="w-full">
    <PageHeader
      title="Import Shopify"
      subtitle="Connectez votre boutique Shopify et consultez votre catalogue"
    />

    <div class="w-full px-3 sm:px-6 py-6 space-y-6">
      <div
        v-if="initialLoading"
        class="bg-white border border-gray-200 rounded-xl p-10 text-center"
      >
        <i class="fas fa-spinner fa-spin text-2xl text-green-600"></i>
      </div>

      <div
        v-else-if="!connection.connected"
        class="bg-white border border-gray-200 rounded-xl p-5 sm:p-6 max-w-2xl"
      >
        <h2 class="text-lg font-semibold text-black mb-2">
          Connecter votre boutique Shopify
        </h2>
        <p class="text-sm text-gray-600 mb-2">
          L'installation commence sur Shopify. Vous y choisissez votre boutique,
          vous autorisez eFashion, puis vous revenez ici.
        </p>
        <button
          type="button"
          class="mb-5 inline-flex items-center gap-2 text-sm font-medium text-blue-600 hover:text-blue-800"
          @click="showConnectHelp = true"
        >
          <i class="fas fa-info-circle"></i>
          Comment fonctionne la connexion Shopify ?
        </button>
        <ActionButton
          v-if="publicInstallUrl"
          label="Installer sur Shopify"
          icon="fab fa-shopify"
          variant="success"
          @click="installFromShopify"
        />
        <p v-else class="text-sm text-gray-600">
          Le lien d'installation sera disponible lorsque Shopify aura validé
          l'application.
        </p>

        <details class="mt-6 border-t border-gray-200 pt-4">
          <summary class="cursor-pointer text-sm text-gray-600">
            Connexion de la boutique déjà reliée à eFashion
          </summary>
          <form class="mt-4 space-y-4" @submit.prevent="connect">
            <BaseInput
              v-model.trim="shopDomain"
              label="Nom de la boutique"
              placeholder="ma-boutique ou ma-boutique.myshopify.com"
              required
            />
            <ActionButton
              type="submit"
              label="Connecter avec Shopify"
              icon="fab fa-shopify"
              variant="secondary"
              :loading="connecting"
              :disabled="connecting || !shopDomain"
            />
          </form>
        </details>
      </div>

      <template v-else>
        <div
          class="bg-white border border-gray-200 rounded-xl p-4 sm:p-5 flex flex-col md:flex-row md:items-center md:justify-between gap-4"
        >
          <div>
            <div class="flex items-center gap-2">
              <span class="w-2.5 h-2.5 rounded-full bg-green-500"></span>
              <h2 class="font-semibold text-black">Boutique connectée</h2>
            </div>
            <p class="text-sm text-gray-600 mt-1 break-all">
              {{ connection.shopUrl }}
            </p>
          </div>
          <div class="flex flex-wrap gap-2">
            <ActionButton
              label="Actualiser le catalogue"
              icon="fas fa-sync-alt"
              variant="primary"
              size="sm"
              :loading="catalogSyncing"
              :disabled="catalogSyncing || resettingAllOverrides"
              @click="syncCatalog(catalogReady)"
            />
            <ActionButton
              label="Revenir aux données Shopify"
              icon="fas fa-undo"
              variant="secondary"
              size="sm"
              :loading="resettingAllOverrides"
              :disabled="catalogLoading || resettingAllOverrides"
              @click="showResetAllConfirm = true"
            />
            <ActionButton
              v-if="isAdmin"
              label="Envoyer les commandes"
              icon="fas fa-file-import"
              variant="primary"
              size="sm"
              :loading="importingOrders"
              :disabled="catalogSyncing || resettingAllOverrides || importingOrders"
              @click="exportOrders"
            />
            <ActionButton
              label="Synchroniser les produits"
              icon="fas fa-download"
              variant="primary"
              size="sm"
              :loading="syncingProducts"
              :disabled="catalogSyncing || resettingAllOverrides || importingOrders || syncingProducts"
              @click="syncLinkedProducts"
            />
            <ActionButton
              label="Configuration Shopify"
              icon="fas fa-cog"
              variant="secondary"
              size="sm"
              :disabled="catalogLoading || resettingAllOverrides"
              @click="showConfigurationModal = true"
            />
            <ActionButton
              label="Déconnecter"
              icon="fas fa-unlink"
              variant="secondary"
              size="sm"
              :disabled="catalogLoading || catalogSyncing || resettingAllOverrides"
              @click="disconnect"
            />
          </div>
        </div>

        <div v-if="catalogSyncing || !catalogReady" class="rounded-lg border border-blue-200 bg-blue-50 p-4 text-sm text-blue-800" role="status">
          <i v-if="catalogSyncing" class="fas fa-spinner fa-spin mr-2"></i>
          {{ catalogSyncing ? `Synchronisation Shopify : ${catalogScanned} produit(s) enregistrés…` : "Le catalogue local n'est pas encore synchronisé. Cliquez sur Actualiser le catalogue pour reprendre." }}
        </div>

        <div
          v-if="catalogError"
          class="p-4 bg-red-50 border border-red-200 rounded-lg text-sm text-red-700"
        >
          <i class="fas fa-exclamation-circle mr-2"></i>{{ catalogError }}
        </div>

        <div
          class="sticky top-[70px] z-30 bg-white/95 backdrop-blur border border-gray-200 rounded-lg px-3 py-2 flex flex-wrap items-center gap-2 shadow-sm"
        >
          <span class="text-xs text-gray-500 shrink-0 whitespace-nowrap">
            {{ filteredProductCount }} affiché(s)
          </span>

          <form class="flex flex-1 items-center gap-2 min-w-[200px] max-w-lg" @submit.prevent="submitReferenceSearch">
            <label for="shopify-reference-search" class="sr-only">Rechercher un SKU</label>
            <div class="relative flex-1 min-w-0">
              <i class="fas fa-search absolute left-2.5 top-1/2 -translate-y-1/2 text-gray-400 text-[10px]"></i>
              <input id="shopify-reference-search" v-model.trim="referenceSearch" type="text"
                autocomplete="off" placeholder="Contient le SKU…"
                class="w-full h-8 pl-7 pr-7 border border-gray-300 rounded-md text-xs bg-white focus:outline-none focus:ring-1 focus:ring-green-500 focus:border-green-500" />
              <button v-if="referenceSearch || appliedSearch" type="button" aria-label="Effacer la recherche"
                class="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-700"
                @click="clearReferenceSearch"><i class="fas fa-times text-[10px]"></i></button>
            </div>
            <button type="submit" class="h-8 rounded-md bg-blue-600 px-3 text-xs font-medium text-white disabled:opacity-50"
              :disabled="catalogLoading">Rechercher</button>
          </form>

          <div class="flex items-center gap-1.5 shrink-0 ml-auto">
            <ActionButton
              label="Préparer tous les produits prêts"
              icon="fas fa-magic"
              variant="success"
              size="sm"
              :loading="previewLoading || (importingProducts && showPrepareConfirm)"
              :disabled="previewLoading || importingProducts || catalogLoading || !catalogReady || !shopifySettings.setupCompleted"
              @click="openPrepareAllConfirm"
            />
            <ActionButton
              :label="`Modifier les fiches en masse (${selectedProductIds.length})`"
              icon="fas fa-layer-group"
              variant="primary"
              size="sm"
              :disabled="selectedProductIds.length === 0"
              @click="openBulkComplete"
            />
            <ActionButton
              :label="`Mettre en ligne (${selectedPublishableCount})`"
              icon="fas fa-cloud-upload-alt"
              variant="success"
              size="sm"
              :loading="importingProducts && !showPublishConfirm"
              :disabled="importingProducts || selectedPublishableCount === 0"
              @click="showPublishConfirm = true"
            />
          </div>
        </div>

        <section
          class="bg-white border border-gray-200 rounded-xl overflow-hidden"
        >
          <div class="p-4 sm:p-5 border-b border-gray-200">
            <h2 class="font-semibold text-black">
              {{
                appliedSearch
                  ? "Résultats de recherche"
                  : "Nouveaux produits"
              }}
              <span class="text-sm font-normal text-gray-500">
                ({{ filteredNewProducts.length }})
              </span>
            </h2>
            <p class="text-sm text-gray-600 mt-1">
              {{
                appliedSearch
                  ? "Correspondances exactes (contient) sur tout le catalogue Shopify."
                  : "Cochez plusieurs produits pour les compléter en masse, ou cliquez sur une ligne pour éditer une fiche."
              }}
            </p>
          </div>
          <UnifiedPagination
            v-if="filteredNewProducts.length > 0 || catalog.hasMore"
            variant="dashboard"
            :total-items="Math.max(filteredNewProducts.length, paginationTotal)"
            :current-page="page"
            :items-per-page="pageSize"
            :items-per-page-options="catalogPageSizeOptions"
            :show-items-per-page="true"
            item-label="produits Shopify"
            @page-change="changePage"
            @items-per-page-change="changePageSize"
          />
          <div
            v-if="catalogLoading && !(catalog.newProducts?.length)"
            class="p-10 text-center text-gray-500"
          >
            <i class="fas fa-spinner fa-spin mr-2"></i>
            Chargement du catalogue…
          </div>
          <div v-else class="overflow-x-auto">
            <table class="min-w-[1100px] w-full text-sm">
              <thead class="bg-gray-50 text-left text-gray-600">
                <tr>
                  <th class="px-3 py-3 w-10 sticky left-0 bg-gray-50 z-10">
                    <input
                      type="checkbox"
                      :checked="allVisibleNewSelected"
                      :disabled="paginatedNewProducts.length === 0"
                      aria-label="Sélectionner tous les produits de la page"
                      class="w-4 h-4 rounded border-gray-300"
                      @change="toggleAllVisibleNewProducts"
                    />
                  </th>
                  <th class="px-3 py-3 whitespace-nowrap">État</th>
                  <th class="px-3 py-3 w-14">Photo</th>
                  <th class="px-3 py-3 whitespace-nowrap">SKU</th>
                  <th class="px-3 py-3 min-w-[180px]">Titre</th>
                  <th class="px-3 py-3 whitespace-nowrap">Marque</th>
                  <th class="px-3 py-3 whitespace-nowrap">Catégorie</th>
                  <th class="px-3 py-3 whitespace-nowrap text-right">
                    Prix HT
                  </th>
                  <th class="px-3 py-3 whitespace-nowrap text-right">Poids</th>
                  <th class="px-3 py-3 whitespace-nowrap">Vendu par</th>
                  <th class="px-3 py-3 min-w-[120px]">Couleurs</th>
                  <th class="px-3 py-3 min-w-[100px]">Tailles</th>
                  <th class="px-3 py-3 whitespace-nowrap text-center">
                    Photos
                  </th>
                  <th class="px-3 py-3 whitespace-nowrap">Statut Shopify</th>
                  <th class="px-3 py-3 min-w-[160px]">Manquants</th>
                </tr>
              </thead>
              <tbody class="divide-y divide-gray-100">
                <tr
                  v-for="product in paginatedNewProducts"
                  :key="`new-${product.id}`"
                  class="group cursor-pointer hover:bg-green-50/60"
                  :title="
                    product.readyForMel
                      ? 'Cliquer pour vérifier la fiche'
                      : 'Cliquer pour compléter la fiche'
                  "
                  @click="openProduct(product)"
                >
                  <td
                    class="px-3 py-3 sticky left-0 bg-white group-hover:bg-green-50/60"
                    @click.stop
                  >
                    <input
                      type="checkbox"
                      :checked="selectedProductIds.includes(product.id)"
                      title="Sélectionner ce produit"
                      class="w-4 h-4 rounded border-gray-300"
                      @change="toggleProductSelection(product.id, product)"
                    />
                  </td>
                  <td class="px-3 py-3">
                    <span
                      class="inline-flex items-center gap-1.5 text-xs font-medium"
                      :class="
                        product.readyForMel ? 'text-green-700' : 'text-red-700'
                      "
                      :title="
                        product.readyForMel
                          ? 'Prêt pour la MEL'
                          : 'Fiche incomplète'
                      "
                    >
                      <span
                        class="inline-block w-2.5 h-2.5 rounded-full"
                        :class="
                          product.readyForMel ? 'bg-green-500' : 'bg-red-500'
                        "
                      ></span>
                      {{ product.readyForMel ? "Prêt" : "Incomplet" }}
                    </span>
                  </td>
                  <td class="px-3 py-3">
                    <img
                      v-if="productCoverUrl(product)"
                      :src="productCoverUrl(product)!"
                      :alt="product.name || product.reference"
                      class="w-10 h-10 rounded object-cover border border-gray-200"
                    />
                    <div
                      v-else
                      class="w-10 h-10 rounded bg-gray-100 border border-gray-200 flex items-center justify-center text-gray-400"
                    >
                      <i class="fas fa-image text-xs"></i>
                    </div>
                  </td>
                  <td
                    class="px-3 py-3 font-medium text-black whitespace-nowrap"
                  >
                    {{ product.reference || "—" }}
                  </td>
                  <td class="px-3 py-3 text-black">
                    {{ product.name || "—" }}
                  </td>
                  <td class="px-3 py-3 text-gray-700 whitespace-nowrap">
                    {{ product.brandName || "—" }}
                    <span v-if="product.pendingBrands?.length" class="ml-1 text-xs text-red-700">mapping à créer</span>
                  </td>
                  <td class="px-3 py-3 text-gray-700">
                    {{ product.categoryName || "—" }}
                    <span v-if="product.pendingCategories?.length" class="ml-1 text-xs text-red-700">mapping à créer</span>
                  </td>
                  <td
                    class="px-3 py-3 text-right text-gray-700 whitespace-nowrap"
                  >
                    {{ formatPrice(product.price) }}
                  </td>
                  <td
                    class="px-3 py-3 text-right text-gray-700 whitespace-nowrap"
                  >
                    {{ formatWeight(product.weight) }}
                  </td>
                  <td class="px-3 py-3 text-gray-700 whitespace-nowrap">
                    {{ product.soldByLabel || "—" }}
                  </td>
                  <td class="px-3 py-3 text-gray-700">
                    {{ product.colorsLabel || "—" }}
                  </td>
                  <td class="px-3 py-3 text-gray-700">
                    {{ product.sizesLabel || "—" }}
                  </td>
                  <td class="px-3 py-3 text-center text-gray-700">
                    {{ product.photosCount }}
                  </td>
                  <td class="px-3 py-3">
                    <span
                      class="px-2 py-1 rounded-full text-xs font-medium whitespace-nowrap"
                      :class="
                        product.active
                          ? 'bg-green-100 text-green-700'
                          : 'bg-gray-100 text-gray-700'
                      "
                    >
                      {{ product.active ? "En ligne" : "Hors ligne" }}
                    </span>
                  </td>
                  <td class="px-3 py-3 text-gray-600 max-w-[220px]">
                    <span
                      v-if="!(product.missingFields?.length)"
                      class="text-green-700"
                    >
                      Fiche complète
                    </span>
                    <span
                      v-else
                      class="line-clamp-2"
                      :title="(product.missingFields ?? []).join(', ')"
                    >
                      {{ (product.missingFields ?? []).join(", ") }}
                    </span>
                  </td>
                </tr>
                <tr v-if="!catalogLoading && filteredNewProducts.length === 0">
                  <td colspan="15" class="px-4 py-8 text-center text-gray-500">
                    Aucun nouveau produit sur cette page.
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
          <UnifiedPagination
            v-if="filteredNewProducts.length > 0 || catalog.hasMore"
            variant="dashboard"
            :total-items="Math.max(filteredNewProducts.length, paginationTotal)"
            :current-page="page"
            :items-per-page="pageSize"
            :items-per-page-options="catalogPageSizeOptions"
            :show-items-per-page="true"
            item-label="produits Shopify"
            @page-change="changePage"
            @items-per-page-change="changePageSize"
          />
        </section>
      </template>
    </div>

    <ShopifyMelProductSheet
      v-model="editedProduct"
      v-model:catalog="catalog"
      :reload-catalog="loadCatalog"
      @success="success"
      @error="error"
    />

    <ShopifyMelBulkSheet ref="bulkSheetRef" />

    <ShopifyConfigurationModal
      v-model:visible="showConfigurationModal"
      :shop-url="connection.shopUrl"
      :required-setup="!shopifySettings.setupCompleted"
      :catalog-ready="catalogReady"
      :catalog-revision="catalogRevision"
      :pending-colors="pendingColorLabels"
      :pending-categories="pendingCategoryLabels"
      :pending-compositions="pendingCompositionLabels"
      :pending-brands="pendingBrandLabels"
      @changed="loadCatalog"
      @settings-saved="onSettingsSaved"
    />

    <ConfirmModal
      :show="showPublishConfirm"
      title="Confirmer la mise en ligne"
      :attention="publishConfirmAttention"
      :message="publishConfirmMessage"
      :reminder="publishConfirmReminder"
      confirm-text="Mettre en ligne"
      loading-text="Mise en ligne…"
      :references="selectedPublishableReferences"
      :confirm-loading="importingProducts"
      :progress-label="importProgressLabel"
      :show-attention-icon="true"
      :lock-background-scroll="true"
      max-width-class="max-w-lg"
      @confirm="publishSelectedProducts"
      @cancel="closePublishConfirm"
    />

    <ConfirmModal
      :show="showPrepareConfirm"
      title="Préparer tous les produits prêts"
      :message="prepareConfirmMessage"
      :reminder="prepareConfirmReminder"
      confirm-text="Préparer les produits"
      loading-text="Préparation en cours…"
      :confirm-loading="importingProducts"
      :progress-label="importProgressLabel"
      :show-attention-icon="true"
      :lock-background-scroll="true"
      max-width-class="max-w-lg"
      @confirm="prepareAllReadyProducts"
      @cancel="closePrepareConfirm"
    />

    <ConfirmModal
      :show="showResetAllConfirm"
      title="Revenir aux données Shopify ?"
      message="Les fiches reprennent les données Shopify du moment : prix, poids, photos, couleurs, composition, catégorie et marque."
      confirm-text="Réinitialiser tout"
      confirm-button-class="bg-red-600 hover:bg-red-700 focus:ring-red-500"
      loading-text="Réinitialisation…"
      :confirm-loading="resettingAllOverrides"
      :show-attention-icon="true"
      :lock-background-scroll="true"
      max-width-class="max-w-lg"
      @confirm="resetAllOverrides"
      @cancel="closeResetAllConfirm"
    />

    <div
      v-if="showConnectHelp"
      class="fixed inset-0 z-50 flex items-center justify-center p-4"
    >
      <button
        type="button"
        class="absolute inset-0 bg-black/40"
        aria-label="Fermer"
        @click="showConnectHelp = false"
      ></button>
      <div
        class="relative bg-white rounded-xl shadow-xl max-w-lg w-full p-6"
        role="dialog"
        aria-labelledby="shopify-connect-help-title"
      >
        <h3
          id="shopify-connect-help-title"
          class="text-lg font-semibold text-black mb-4"
        >
          Connexion Shopify
        </h3>
        <ol class="list-decimal list-inside space-y-2 text-sm text-gray-700">
          <li>Cliquez sur « Installer sur Shopify ».</li>
          <li>Choisissez votre boutique sur la page Shopify.</li>
          <li>
            Autorisez eFashion à lire votre catalogue (produits, variantes,
            stock).
          </li>
          <li>
            Revenez sur eFashion et connectez-vous : la boutique se rattache
            à votre compte.
          </li>
        </ol>
        <p class="mt-4 text-sm text-gray-600">
          Vous n’avez pas besoin de créer une application dans votre admin
          Shopify ni de copier une clé API.
        </p>
        <div class="mt-6 flex justify-end">
          <ActionButton
            label="Compris"
            variant="primary"
            size="sm"
            @click="showConnectHelp = false"
          />
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, onMounted, provide, ref } from "vue";
import { useRoute, useRouter } from "vue-router";
import PageHeader from "@/components/layout/seller/PageHeader.vue";
import BaseInput from "@/components/forms/BaseInput.vue";
import ActionButton from "@/components/ui/ActionButton.vue";
import ConfirmModal from "@/components/common/ConfirmModal.vue";
import UnifiedPagination from "@/components/ui/UnifiedPagination.vue";
import ShopifyMelProductSheet from "@/components/shopify/ShopifyMelProductSheet.vue";
import ShopifyMelBulkSheet from "@/components/shopify/ShopifyMelBulkSheet.vue";
import ShopifyConfigurationModal from "@/components/shopify/ShopifyConfigurationModal.vue";
import { useAuthStore } from "@/stores/auth";
import { useNotification } from "@/composables/useNotification";
import { useBodyScrollLock } from "@/composables/useBodyScrollLock";
import type { ShopifyMelBulkSheetContext } from "@/composables/useShopifyMelBulkSheet";
import {
  disconnectShopify,
  getShopifyAuthorizeUrl,
  claimShopifyInstall,
  exportShopifyOrders,
  syncLinkedProductsFromShopify,
  getShopifyConnection,
  getShopifyCatalogStatus,
  getShopifyImportPreview,
  getShopifyProducts,
  importShopifyProductRowsInBatches,
  importShopifyProductsInBatches,
  toShopifyImportRow,
  resetAllShopifyProductOverrides,
  getShopifySettings,
  resolveShopifyCoverImageUrl,
  SHOPIFY_IMPORT_BATCH_SIZE,
  syncShopifyCatalogNextPage,
} from "@/api/shopify";
import type { ShopifyImportPreview } from "@/api/shopify";
import type {
  ShopifyCatalogPage,
  ShopifyConnection,
  ShopifyProduct,
} from "@/types/shopify";

const authStore = useAuthStore();
const isAdmin = computed(() => authStore.isImpersonating);

const emptyCatalog = (): ShopifyCatalogPage => ({
  items: [],
  newProducts: [],
  total: null,
  hasMore: false,
  endCursor: null,
});

function normalizeProduct(raw: Partial<ShopifyProduct>): ShopifyProduct {
  return {
    id: raw.id ?? "",
    legacyResourceId: raw.legacyResourceId ?? "",
    title: raw.title ?? "",
    handle: raw.handle ?? "",
    vendor: raw.vendor ?? "",
    productType: raw.productType ?? "",
    shopifyCategoryName: raw.shopifyCategoryName ?? "",
    status: raw.status ?? "",
    tags: Array.isArray(raw.tags) ? raw.tags : [],
    createdAt: raw.createdAt ?? "",
    updatedAt: raw.updatedAt ?? "",
    description: raw.description ?? "",
    descriptionHtml: raw.descriptionHtml ?? "",
    descriptions: raw.descriptions ?? {},
    totalInventory: raw.totalInventory ?? 0,
    variantsCount: raw.variantsCount ?? 0,
    imageUrl: raw.imageUrl ?? null,
    imageIds: Array.isArray(raw.imageIds) ? raw.imageIds : [],
    images: Array.isArray(raw.images)
      ? raw.images.map((image) => ({
          id: String(image.id),
          url: String(image.url || ""),
          altText: image.altText ?? null,
          colorName: image.colorName ?? null,
          sourceColorName: image.sourceColorName ?? image.colorName ?? null,
        }))
      : [],
    reference: raw.reference ?? raw.handle ?? "",
    barcode: raw.barcode ?? null,
    name: raw.name ?? raw.title ?? "",
    active: raw.active ?? raw.status === "ACTIVE",
    price: raw.price ?? 0,
    shopifyPrice: raw.shopifyPrice ?? raw.price ?? 0,
    wholesalePrice: raw.wholesalePrice ?? 0,
    shopifyWeight: raw.shopifyWeight ?? 0,
    weight: raw.weight ?? 0,
    options: Array.isArray(raw.options) ? raw.options : [],
    variants: Array.isArray(raw.variants) ? raw.variants : [],
    collectionNames: Array.isArray(raw.collectionNames)
      ? raw.collectionNames
      : [],
    compositionLabel: raw.compositionLabel ?? "",
    metafields: Array.isArray(raw.metafields) ? raw.metafields : [],
    source: raw.source === "existing" ? "existing" : "new",
    efashionProductId: raw.efashionProductId ?? null,
    readyForMel: Boolean(raw.readyForMel),
    missingFields: Array.isArray(raw.missingFields) ? raw.missingFields : [],
    pendingColors: Array.isArray(raw.pendingColors) ? raw.pendingColors : [],
    colorCollisions: Array.isArray(raw.colorCollisions)
      ? raw.colorCollisions.flatMap((collision) => {
          const colorId = Number(collision?.colorId);
          const names = Array.isArray(collision?.names)
            ? collision.names.map((name) => String(name || "").trim()).filter(Boolean)
            : [];
          if (!Number.isInteger(colorId) || colorId <= 0 || names.length < 2) {
            return [];
          }
          return [{
            colorId,
            officialLabel: String(collision?.officialLabel || "").trim() || `couleur #${colorId}`,
            names,
          }];
        })
      : [],
    pendingCategories: Array.isArray(raw.pendingCategories)
      ? raw.pendingCategories
      : [],
    pendingCompositions: Array.isArray(raw.pendingCompositions)
      ? raw.pendingCompositions
      : [],
    pendingBrands: Array.isArray(raw.pendingBrands) ? raw.pendingBrands : [],
    compositionNeedsReview: Boolean(raw.compositionNeedsReview),
    overrides: raw.overrides ?? {},
    autoMappedFields: Array.isArray(raw.autoMappedFields)
      ? raw.autoMappedFields
      : [],
    brandName: raw.brandName ?? raw.vendor ?? "",
    categoryName: raw.categoryName ?? raw.shopifyCategoryName ?? "",
    colorsLabel: raw.colorsLabel ?? "",
    sizesLabel: raw.sizesLabel ?? "",
    soldByLabel: raw.soldByLabel ?? "",
    photosCount: raw.photosCount ?? raw.images?.length ?? 0,
  };
}

function normalizeCatalog(raw: Partial<ShopifyCatalogPage>): ShopifyCatalogPage {
  const items = (raw.items ?? []).map((item) => normalizeProduct(item));
  const newProducts = (raw.newProducts ?? items.filter((item) => item.source === "new")).map(
    (item) => normalizeProduct(item)
  );

  return {
    items,
    newProducts,
    total: raw.total ?? null,
    hasMore: Boolean(raw.hasMore),
    endCursor: raw.endCursor ?? null,
  };
}

const route = useRoute();
const router = useRouter();
const { success, error } = useNotification();

const initialLoading = ref(true);
const connecting = ref(false);
const catalogLoading = ref(false);
const catalogSyncing = ref(false);
const importingOrders = ref(false);
const syncingProducts = ref(false);
const catalogReady = ref(false);
const catalogScanned = ref(0);
const catalogRevision = ref(0);
const importingProducts = ref(false);
const importProgressLabel = ref("");
const previewLoading = ref(false);
const importPreview = ref<ShopifyImportPreview | null>(null);
const showPrepareConfirm = ref(false);
const resettingAllOverrides = ref(false);
const showPublishConfirm = ref(false);
const showResetAllConfirm = ref(false);
const showConnectHelp = ref(false);
const showConfigurationModal = ref(false);
const shopifySettings = ref<import("@/api/shopify").ShopifySettings>({
  defaultGender: null,
  defaultProvenanceId: null,
  setupCompleted: false,
  autoSyncFromShopify: false,
  autoSyncOrdersToShopify: true,
});
const catalogError = ref("");
const shopDomain = ref("");
const publicInstallUrl = ref<string | null>(null);
const referenceSearch = ref("");
const appliedSearch = ref("");
const page = ref(1);
const pageSize = ref(50);
const catalogPageSizeOptions = [25, 50, 100, 200];
const newTablePage = ref(1);
const newTablePageSize = ref(20);
const connection = ref<ShopifyConnection>({ connected: false });
const catalog = ref<ShopifyCatalogPage>(emptyCatalog());
const editedProduct = ref<ShopifyProduct | null>(null);
const selectedProductIds = ref<string[]>([]);
const selectedProductsById = ref(new Map<string, ShopifyProduct>());
const showBulkComplete = ref(false);
const bulkSheetRef = ref<InstanceType<typeof ShopifyMelBulkSheet> | null>(
  null
);

provide<ShopifyMelBulkSheetContext>("shopifyMelBulkContext", {
  selectedProductIds,
  selectedProductsById,
  onReloadCatalog: () => loadCatalog(),
  success,
  error,
  showBulkComplete,
});

function openBulkComplete() {
  bulkSheetRef.value?.openBulkComplete();
}

const isModalOpen = computed(
  () =>
    showConnectHelp.value ||
    showConfigurationModal.value ||
    editedProduct.value !== null ||
    showBulkComplete.value ||
    showPrepareConfirm.value ||
    showPublishConfirm.value
);
useBodyScrollLock(isModalOpen);

const pendingColorLabels = computed(() => Array.from(new Set(catalog.value.items.flatMap((product) => product.pendingColors || []))));
const pendingCategoryLabels = computed(() => Array.from(new Set(catalog.value.items.flatMap((product) => product.pendingCategories || []))));
const pendingCompositionLabels = computed(() => Array.from(new Set(catalog.value.items.flatMap((product) => product.pendingCompositions || []))));
const pendingBrandLabels = computed(() => Array.from(new Set(catalog.value.items.flatMap((product) => product.pendingBrands || []))));

const paginationTotal = computed(() => {
  const items = catalog.value.items ?? [];
  return (
    catalog.value.total ??
    (page.value - 1) * pageSize.value +
      items.length +
      (catalog.value.hasMore ? 1 : 0)
  );
});
const filteredNewProducts = computed(() => {
  // En recherche : afficher toutes les correspondances (new + already imported).
  // Sinon : uniquement les nouveaux à importer.
  if (appliedSearch.value) {
    return catalog.value.items ?? [];
  }
  return catalog.value.newProducts ?? [];
});
const paginatedNewProducts = computed(() => filteredNewProducts.value);
const filteredProductCount = computed(
  () => filteredNewProducts.value.length
);
const allVisibleNewSelected = computed(
  () =>
    paginatedNewProducts.value.length > 0 &&
    paginatedNewProducts.value.every((product) =>
      selectedProductIds.value.includes(product.id)
    )
);

const bulkSelectionProducts = computed(() =>
  selectedProductIds.value
    .map((productId) => selectedProductsById.value.get(productId))
    .filter((product): product is ShopifyProduct => Boolean(product))
);

const selectedPublishableCount = computed(
  () =>
    bulkSelectionProducts.value.filter((product) => product.readyForMel).length
);

const selectedActiveImportCount = computed(
  () =>
    bulkSelectionProducts.value.filter(
      (product) => product.readyForMel && product.active
    ).length
);

const selectedDraftImportCount = computed(
  () =>
    bulkSelectionProducts.value.filter(
      (product) => product.readyForMel && !product.active
    ).length
);

const selectedPublishableReferences = computed(() =>
  bulkSelectionProducts.value
    .filter((product) => product.readyForMel)
    .map((product) => {
      const reference = product.reference?.trim();
      const name = product.name?.trim();
      const label =
        reference && name
          ? `${reference} · ${name}`
          : reference || name || product.id;
      return {
        label,
        meta: formatPrice(productEffectivePrice(product)),
      };
    })
);

const publishBatchCount = computed(() =>
  Math.ceil(selectedPublishableCount.value / SHOPIFY_IMPORT_BATCH_SIZE)
);

const publishConfirmAttention =
  "Attention : merci de bien vérifier vos fiches avant de continuer (photos, catégories, compositions, couleurs, stocks, mode de vente, prix HT grossiste, etc.).";

const publishConfirmReminder = computed(() => {
  const priceHint =
    "Rappel : les prix Shopify sont souvent orientés B2C ; assurez-vous que le prix grossiste HT est correct.";
  const draftCount = selectedDraftImportCount.value;
  if (!draftCount) return priceHint;
  const draftHint =
    draftCount === 1
      ? "1 produit est hors ligne sur Shopify : il sera importé en brouillon sur eFashion, sans publication."
      : `${draftCount} produits sont hors ligne sur Shopify : ils seront importés en brouillon sur eFashion, sans publication.`;
  return `${priceHint}\n\n${draftHint}`;
});

const prepareConfirmMessage = computed(() => {
  const preview = importPreview.value;
  if (!preview) return "Aucun produit à préparer.";
  return `${publishConfirmAttention}\n\nSur ${preview.scanned} produits Shopify : ${preview.ready} prêt(s) à importer (${preview.active} à publier, ${preview.draft} en brouillon), ${preview.existing} déjà présent(s) sur eFashion et ${preview.incomplete} incomplet(s).${preview.duplicateReferences ? ` ${preview.duplicateReferences} référence(s) en doublon seront ignorées.` : ""}\n\nLes produits prêts seront traités par lots de ${SHOPIFY_IMPORT_BATCH_SIZE}. Les produits déjà importés ne seront pas recréés.`;
});

const prepareConfirmReminder = computed(() => {
  const priceHint = "Vérifiez les prix grossistes HT avant de continuer.";
  return importPreview.value?.draft
    ? `${priceHint}\n\n${importPreview.value.draft} produit(s) hors ligne sur Shopify seront créés en brouillon sur eFashion.`
    : priceHint;
});

const publishConfirmMessage = computed(() => {
  const batchHint =
    publishBatchCount.value > 1
      ? `\n\nL'import sera traité automatiquement par lots de ${SHOPIFY_IMPORT_BATCH_SIZE} produits (${publishBatchCount.value} lots).`
      : "";
  const total = selectedPublishableCount.value;
  const activeCount = selectedActiveImportCount.value;
  const draftCount = selectedDraftImportCount.value;
  if (!total) {
    return `Aucun produit prêt à importer.${batchHint}`;
  }
  if (draftCount === 0) {
    return `Les ${total} produit(s) prêt(s) sélectionné(s) seront importés et publiés directement sur eFashion. Cette action est définitive pour les références concernées.${batchHint}`;
  }
  if (activeCount === 0) {
    return `Les ${total} produit(s) prêt(s) sélectionné(s) sont hors ligne sur Shopify : ils seront importés en brouillon sur eFashion (non publiés).${batchHint}`;
  }
  return `Les ${total} produit(s) prêt(s) sélectionné(s) seront importés sur eFashion : ${activeCount} publié(s) (en ligne sur Shopify) et ${draftCount} en brouillon (hors ligne sur Shopify). Cette action est définitive pour les références concernées.${batchHint}`;
});

function productEffectivePrice(product: ShopifyProduct): number {
  const override = product.overrides?.price;
  if (typeof override === "number" && override > 0) return override;
  if (product.wholesalePrice && product.wholesalePrice > 0) {
    return product.wholesalePrice;
  }
  return product.price;
}

function refreshSelectedProductsCache(products: ShopifyProduct[]) {
  if (!selectedProductIds.value.length) return;
  const byId = new Map(products.map((product) => [product.id, product]));
  for (const productId of selectedProductIds.value) {
    const product = byId.get(productId);
    if (product) selectedProductsById.value.set(productId, product);
  }
}

function removeImportedSelection(productGids: string[]) {
  if (!productGids.length) return;
  const imported = new Set(productGids);
  selectedProductIds.value = selectedProductIds.value.filter(
    (id) => !imported.has(id)
  );
  const next = new Map(selectedProductsById.value);
  for (const productGid of productGids) next.delete(productGid);
  selectedProductsById.value = next;
}

async function publishSelectedProducts() {
  const rows = bulkSelectionProducts.value
    .filter((product) => product.readyForMel)
    .map((product) => toShopifyImportRow(product));
  if (await importReadyRows(rows)) showPublishConfirm.value = false;
}

async function openPrepareAllConfirm() {
  if (previewLoading.value || importingProducts.value) return;
  previewLoading.value = true;
  importPreview.value = null;
  try {
    const preview = await getShopifyImportPreview();
    if (preview.scanIncomplete) {
      error("Le catalogue dépasse 10 000 produits : le balayage est incomplet. Aucun import global n'a démarré.");
    } else if (!preview.ready) {
      success("Aucun nouveau produit prêt à préparer. Complétez les fiches signalées puis réessayez.");
    } else {
      importPreview.value = preview;
      showPrepareConfirm.value = true;
    }
  } catch (err) {
    error(err instanceof Error ? err.message : "Impossible d'analyser le catalogue Shopify.");
  } finally {
    previewLoading.value = false;
  }
}

async function prepareAllReadyProducts() {
  const gids = importPreview.value?.productGids ?? [];
  if (!gids.length || importingProducts.value) return;
  await importReadyProducts(gids);
  showPrepareConfirm.value = false;
  importPreview.value = null;
}

async function importReadyRows(
  rows: ReturnType<typeof toShopifyImportRow>[],
): Promise<boolean> {
  if (!rows.length || importingProducts.value) return false;
  importingProducts.value = true;
  importProgressLabel.value =
    rows.length > SHOPIFY_IMPORT_BATCH_SIZE
      ? `Préparation de l'import (${rows.length} produits)…`
      : "Mise en ligne en cours…";
  const importedGids: string[] = [];
  try {
    const report = await importShopifyProductRowsInBatches(rows, {
      onBatchReport: (batch) => {
        for (const result of batch.results) {
          if (result.success) importedGids.push(result.shopifyProductGid);
        }
      },
      onBatchProgress: ({
        batchIndex,
        batchCount,
        importedSoFar,
        failedSoFar,
      }) => {
        importProgressLabel.value =
          batchCount > 1
            ? `Lot ${batchIndex}/${batchCount} — ${importedSoFar} importé(s), ${failedSoFar} échec(s)`
            : "Mise en ligne en cours…";
      },
    });
    removeImportedSelection(importedGids);
    if (report.imported > 0) {
      const publishedCount = report.results.filter(
        (result) =>
          result.success &&
          (result.visibility
            ? result.visibility === "online"
            : Boolean(result.published))
      ).length;
      const draftCount = report.results.filter(
        (result) =>
          result.success &&
          (result.visibility
            ? result.visibility === "draft"
            : !result.published)
      ).length;
      let summary = `${report.imported} produit(s) importé(s) sur eFashion`;
      if (publishedCount && draftCount) {
        summary += ` (${publishedCount} publié(s), ${draftCount} en brouillon)`;
      } else if (draftCount && !publishedCount) {
        summary += " en brouillon";
      }
      if (report.failed) summary += `, ${report.failed} en échec`;
      const notes = [
        ...new Set(
          report.results
            .filter((result) => result.success && result.message)
            .map((result) => result.message as string)
        ),
      ];
      success(notes.length === 1 ? `${summary}. ${notes[0]}` : `${summary}.`);
    }
    if (report.failed > 0) {
      const messages = report.results
        .filter((result) => !result.success)
        .map(
          (result) =>
            `${result.shopifyProductGid} : ${result.message || "échec"}`
        )
        .join("\n");
      error(messages);
    }
    await loadCatalog();
    return true;
  } catch (err) {
    if (importedGids.length) {
      removeImportedSelection(importedGids);
      await loadCatalog();
    }
    const message = err instanceof Error ? err.message : "Import impossible.";
    error(importedGids.length
      ? `${importedGids.length} produit(s) importé(s) avant l'interruption. Relancez la préparation pour poursuivre. ${message}`
      : message);
    return false;
  } finally {
    importingProducts.value = false;
    importProgressLabel.value = "";
  }
}

async function importReadyProducts(publishableGids: string[]): Promise<boolean> {
  if (!publishableGids.length || importingProducts.value) return false;
  importingProducts.value = true;
  importProgressLabel.value =
    publishableGids.length > SHOPIFY_IMPORT_BATCH_SIZE
      ? `Préparation de l'import (${publishableGids.length} produits)…`
      : "Mise en ligne en cours…";
  const importedGids: string[] = [];
  try {
    const report = await importShopifyProductsInBatches(publishableGids, {
      onBatchReport: (batch) => {
        for (const result of batch.results) {
          if (result.success) importedGids.push(result.shopifyProductGid);
        }
      },
      onBatchProgress: ({
        batchIndex,
        batchCount,
        importedSoFar,
        failedSoFar,
      }) => {
        importProgressLabel.value =
          batchCount > 1
            ? `Lot ${batchIndex}/${batchCount} — ${importedSoFar} importé(s), ${failedSoFar} échec(s)`
            : "Mise en ligne en cours…";
      },
    });
    removeImportedSelection(importedGids);
    if (report.imported > 0) {
      const publishedCount = report.results.filter(
        (result) =>
          result.success &&
          (result.visibility
            ? result.visibility === "online"
            : Boolean(result.published))
      ).length;
      const draftCount = report.results.filter(
        (result) =>
          result.success &&
          (result.visibility
            ? result.visibility === "draft"
            : !result.published)
      ).length;
      let summary = `${report.imported} produit(s) importé(s) sur eFashion`;
      if (publishedCount && draftCount) {
        summary += ` (${publishedCount} publié(s), ${draftCount} en brouillon)`;
      } else if (draftCount && !publishedCount) {
        summary += " en brouillon";
      }
      if (report.failed) summary += `, ${report.failed} en échec`;
      const notes = [
        ...new Set(
          report.results
            .filter((result) => result.success && result.message)
            .map((result) => result.message as string)
        ),
      ];
      success(notes.length === 1 ? `${summary}. ${notes[0]}` : `${summary}.`);
    }
    if (report.failed > 0) {
      const messages = report.results
        .filter((result) => !result.success)
        .map(
          (result) =>
            `${result.shopifyProductGid} : ${result.message || "échec"}`
        )
        .join("\n");
      error(messages);
    }
    await loadCatalog();
    return true;
  } catch (err) {
    if (importedGids.length) {
      removeImportedSelection(importedGids);
      await loadCatalog();
    }
    const message = err instanceof Error ? err.message : "Import impossible.";
    error(importedGids.length
      ? `${importedGids.length} produit(s) importé(s) avant l'interruption. Relancez la préparation pour poursuivre. ${message}`
      : message);
    return false;
  } finally {
    importingProducts.value = false;
    importProgressLabel.value = "";
  }
}

function closePrepareConfirm() {
  if (importingProducts.value) return;
  showPrepareConfirm.value = false;
  importPreview.value = null;
}

function closePublishConfirm() {
  if (importingProducts.value) return;
  showPublishConfirm.value = false;
}

function closeResetAllConfirm() {
  if (resettingAllOverrides.value) return;
  showResetAllConfirm.value = false;
}

async function resetAllOverrides() {
  if (resettingAllOverrides.value || catalogLoading.value) return;
  resettingAllOverrides.value = true;
  try {
    const report = await resetAllShopifyProductOverrides();
    editedProduct.value = null;
    selectedProductIds.value = [];
    selectedProductsById.value = new Map();
    await loadCatalog();
    success(
      report.cleared > 0
        ? `${report.cleared} fiche(s) remise(s) aux données Shopify d’origine.`
        : "Aucune modification à effacer : le catalogue est déjà à l’état Shopify."
    );
    showResetAllConfirm.value = false;
  } catch (err) {
    error(
      err instanceof Error
        ? err.message
        : "Impossible de réinitialiser les fiches Shopify."
    );
  } finally {
    resettingAllOverrides.value = false;
  }
}

function openProduct(product: ShopifyProduct) {
  editedProduct.value = product;
}

function toggleProductSelection(productId: string, product: ShopifyProduct) {
  const index = selectedProductIds.value.indexOf(productId);
  if (index >= 0) {
    selectedProductIds.value = selectedProductIds.value.filter(
      (id) => id !== productId
    );
    selectedProductsById.value.delete(productId);
    return;
  }
  selectedProductIds.value = [...selectedProductIds.value, productId];
  selectedProductsById.value.set(productId, product);
}

function toggleAllVisibleNewProducts(event: Event) {
  const checked = (event.target as HTMLInputElement).checked;
  if (!checked) {
    for (const product of paginatedNewProducts.value) {
      selectedProductIds.value = selectedProductIds.value.filter(
        (id) => id !== product.id
      );
      selectedProductsById.value.delete(product.id);
    }
    return;
  }
  for (const product of paginatedNewProducts.value) {
    if (!selectedProductIds.value.includes(product.id)) {
      selectedProductIds.value = [...selectedProductIds.value, product.id];
      selectedProductsById.value.set(product.id, product);
    }
  }
}

function formatPrice(value: number) {
  if (!(value > 0)) return "—";
  return new Intl.NumberFormat("fr-FR", {
    style: "currency",
    currency: "EUR",
  }).format(value);
}

function formatWeight(value: number) {
  if (!(value > 0)) return "—";
  return `${value.toLocaleString("fr-FR", {
    minimumFractionDigits: 0,
    maximumFractionDigits: 2,
  })} kg`;
}

function productCoverUrl(product: ShopifyProduct) {
  return resolveShopifyCoverImageUrl(product);
}

function installFromShopify() {
  if (!publicInstallUrl.value) return;
  window.location.href = publicInstallUrl.value;
}

async function connect() {
  connecting.value = true;
  try {
    const { authorizeUrl } = await getShopifyAuthorizeUrl(shopDomain.value);
    window.location.href = authorizeUrl;
  } catch (err) {
    error(
      err instanceof Error
        ? err.message
        : "Impossible de démarrer la connexion Shopify."
    );
    connecting.value = false;
  }
}

async function exportOrders() {
  if (!isAdmin.value || importingOrders.value) return;
  importingOrders.value = true;
  try {
    const result = await exportShopifyOrders();
    const failed = result.results.filter((item) => item.action === "failed");
    if (failed.length) {
      error(
        `${result.exported} commande(s) envoyée(s), ${failed.length} en échec : ${failed
          .map((item) => `${item.orderName} (${item.reason || "erreur"})`)
          .join(", ")}`,
      );
      return;
    }
    success(`${result.exported} commande(s) envoyée(s) vers Shopify.`);
  } catch (err) {
    error(err instanceof Error ? err.message : "Envoi des commandes vers Shopify impossible.");
  } finally {
    importingOrders.value = false;
  }
}

async function syncLinkedProducts() {
  if (syncingProducts.value) return;
  syncingProducts.value = true;
  let updated = 0;
  let skipped = 0;
  let failed = 0;
  let afterId = 0;
  const notes: string[] = [];
  try {
    do {
      const result = await syncLinkedProductsFromShopify(afterId);
      updated += result.updated;
      skipped += result.skipped;
      failed += result.failed;
      for (const item of result.results) {
        if (item.reason) notes.push(item.reason);
      }
      afterId = result.nextAfterId ?? 0;
    } while (afterId);
    if (failed || skipped || notes.length) {
      error(`${updated} produit(s) synchronisé(s), ${skipped} ignoré(s), ${failed} en échec. ${notes.slice(0, 3).join(' ')}`.trim());
      return;
    }
    success(`${updated} produit(s) déjà associé(s) mis à jour depuis Shopify.`);
  } catch (err) {
    error(err instanceof Error ? err.message : "Synchronisation des produits impossible.");
  } finally {
    syncingProducts.value = false;
  }
}

async function disconnect() {
  if (!window.confirm("Déconnecter cette boutique Shopify ?")) return;
  try {
    await disconnectShopify();
    connection.value = { connected: false };
    catalogReady.value = false;
    catalog.value = emptyCatalog();
    referenceSearch.value = "";
    appliedSearch.value = "";
    success("Boutique Shopify déconnectée.");
  } catch (err) {
    error(
      err instanceof Error
        ? err.message
        : "Impossible de déconnecter la boutique Shopify."
    );
  }
}

let catalogRequestSequence = 0;
async function loadCatalog() {
  if (!connection.value.connected) return;
  const sequence = ++catalogRequestSequence;
  catalogLoading.value = true;
  catalogError.value = "";
  try {
    let nextCatalog = normalizeCatalog(
      await getShopifyProducts(
        page.value,
        pageSize.value,
        appliedSearch.value || undefined
      )
    );
    if (sequence !== catalogRequestSequence) return;
    const visibleCount = appliedSearch.value
      ? (nextCatalog.items?.length ?? 0)
      : (nextCatalog.newProducts?.length ?? 0);
    if (!appliedSearch.value && page.value > 1 && visibleCount === 0) {
      const total = nextCatalog.total ?? 0;
      const lastPage = Math.max(1, Math.ceil(total / pageSize.value));
      if (lastPage !== page.value) {
        page.value = lastPage;
        nextCatalog = normalizeCatalog(
          await getShopifyProducts(
            page.value,
            pageSize.value,
            appliedSearch.value || undefined
          )
        );
        if (sequence !== catalogRequestSequence) return;
      }
    }
    catalog.value = nextCatalog;
    refreshSelectedProductsCache(catalog.value.newProducts ?? []);
    resetTablePages();
  } catch (err) {
    if (sequence !== catalogRequestSequence) return;
    catalogError.value =
      err instanceof Error
        ? err.message
        : "Impossible de charger le catalogue Shopify.";
  } finally {
    if (sequence === catalogRequestSequence) catalogLoading.value = false;
  }
}

async function syncCatalog(restart = false) {
  if (catalogSyncing.value || !connection.value.connected) return;
  catalogSyncing.value = true;
  catalogError.value = "";
  try {
    let next = await syncShopifyCatalogNextPage(restart);
    catalogScanned.value = next.scanned;
    while (!next.done) {
      next = await syncShopifyCatalogNextPage();
      catalogScanned.value = next.scanned;
    }
    catalogReady.value = next.ready;
    catalogRevision.value += 1;
    page.value = 1;
    await loadCatalog();
    if (restart) success(`${next.scanned} produit(s) synchronisés depuis Shopify.`);
  } catch (caught) {
    catalogError.value = caught instanceof Error ? caught.message : "Synchronisation Shopify impossible.";
  } finally {
    catalogSyncing.value = false;
  }
}

function onSettingsSaved(settings: import("@/api/shopify").ShopifySettings) {
  const initialSetup = !shopifySettings.value.setupCompleted;
  shopifySettings.value = settings;
  if (initialSetup) showConfigurationModal.value = false;
}

async function changePage(nextPage: number) {
  page.value = nextPage;
  resetTablePages();
  await loadCatalog();
}

async function changePageSize(nextSize: number) {
  pageSize.value = nextSize;
  page.value = 1;
  resetTablePages();
  await loadCatalog();
}

function resetTablePages() {
  newTablePage.value = 1;
}

async function submitReferenceSearch() {
  const search = referenceSearch.value.trim();
  if (search === appliedSearch.value) return;
  appliedSearch.value = search;
  page.value = 1;
  resetTablePages();
  await loadCatalog();
}

async function clearReferenceSearch() {
  referenceSearch.value = "";
  if (!appliedSearch.value) return;
  appliedSearch.value = "";
  page.value = 1;
  resetTablePages();
  await loadCatalog();
}

async function handleOAuthReturn() {
  const pending = route.query.pending;
  const connected = route.query.connected;
  const oauthError = route.query.error;

  if (typeof pending === "string" && pending) {
    try {
      await claimShopifyInstall(pending);
      success("Boutique Shopify connectée.");
    } catch (err) {
      error(
        err instanceof Error
          ? err.message
          : "Impossible de terminer la connexion Shopify."
      );
    }
    await router.replace({ name: "services/shopify" });
    return;
  }

  if (!connected && !oauthError) return;

  if (connected === "1") {
    success("Boutique Shopify connectée.");
  } else if (typeof oauthError === "string" && oauthError) {
    error(oauthError);
  }

  await router.replace({ name: "services/shopify" });
}

onMounted(async () => {
  await handleOAuthReturn();
  try {
    connection.value = await getShopifyConnection();
    publicInstallUrl.value = connection.value.publicInstallUrl || null;
    if (connection.value.connected) {
      shopDomain.value = connection.value.shopUrl || "";
      shopifySettings.value = await getShopifySettings();
      const status = await getShopifyCatalogStatus();
      catalogReady.value = status.ready;
      catalogScanned.value = status.scanned;
      await loadCatalog();
      if (!shopifySettings.value.setupCompleted) showConfigurationModal.value = true;
      if (!status.ready || status.syncing) void syncCatalog();
    }
  } catch (err) {
    error(
      err instanceof Error
        ? err.message
        : "Impossible de charger la connexion Shopify."
    );
  } finally {
    initialLoading.value = false;
  }
});
</script>
